import { E as EVENTS } from './events-58bc9098.js';

class ProductBlockVariantSelector extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      variantButton: "[js-variant-button]",
      variantGroup: "[js-variant-group-container]",
      variantLabelValue: "[js-variant-label-value]",
      variantOption: "[js-variant-option]",
      variantInput: "[js-variant-input]",
      noJsVariantSelector: "[js-no-js-variant-selector]",
    };

    this.labelTranslations = {
      "available product": "Produkt",
      "color": "Farbe",
      "size": "Größe",
    };

    this.onVariantChange       = this.onVariantChange.bind(this);
    this.onVariantChangeA11y   = this.onVariantChangeA11y.bind(this);
    this.handleRepaint         = this.handleRepaint.bind(this);
    this.resolveVariantInput   = this.resolveVariantInput.bind(this);
  }

  connectedCallback() {
    this.controller = new AbortController();
    this.initActiveVariants();

    this.addEventListener(
      EVENTS.PRODUCT_VARIANT_CHANGED,
      this.handleRepaint,
      { signal: this.controller.signal },
    );
  }

  initActiveVariants() {
    this.activeVariantsController = new AbortController();

    this.noJsVariantSelector = this.querySelector(this.selectors.noJsVariantSelector);
    this.noJsVariantSelector?.remove();

    this.variantInputs = this.querySelectorAll(this.selectors.variantInput);
    this.variantInputs.forEach((input) => {
      input.addEventListener("change", this.onVariantChange, {
        signal: this.activeVariantsController.signal,
      });
    });

    this.toggleSingleChipGroups();

    this.translateOptionGroupLabels();

    this.variantButton = this.querySelectorAll(this.selectors.variantButton);
    this.variantButton.forEach((button) => {
      button.addEventListener(
        "keydown",
        (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();

            const inputHtml = this.resolveVariantInput(event.target);
            if (inputHtml) this.onVariantChangeA11y(inputHtml);
          }
        },
        { signal: this.activeVariantsController.signal },
      );
    });
  }

  translateOptionGroupLabels() {
    const labelEls = this.querySelectorAll('.option-group__label .label-text');

    labelEls.forEach((el) => {
      const original = (el.textContent || '').trim();
      if (!original) return;

      const hadColon = original.endsWith(':');
      const normalizedKey = original.replace(/:\s*$/, '').trim().toLowerCase();
      const tr = this.labelTranslations[normalizedKey];

      if (tr) el.textContent = tr + (hadColon ? ':' : '');
    });
  }

  toggleSingleChipGroups() {
    const groups = this.querySelectorAll(this.selectors.variantGroup);

    groups.forEach((group) => {
      const chipsContainer = group.querySelector('.option-group__chips-container');
      if (!chipsContainer) return;

      const chips = chipsContainer.querySelectorAll('[js-chip]');
      const count = chips.length;

      if (count <= 1) {
        chipsContainer.setAttribute('hidden', '');
        chipsContainer.setAttribute('aria-hidden', 'true');

        const focusables = chipsContainer.querySelectorAll(
          `${this.selectors.variantInput}, ${this.selectors.variantButton}, ${this.selectors.variantOption}, [js-chip]`
        );
        focusables.forEach((el) => {
          el.setAttribute('tabindex', '-1');
          el.style.pointerEvents = 'none';
        });
      } else {
        chipsContainer.removeAttribute('hidden');
        chipsContainer.removeAttribute('aria-hidden');

        const focusables = chipsContainer.querySelectorAll(
          `${this.selectors.variantInput}, ${this.selectors.variantButton}, ${this.selectors.variantOption}, [js-chip]`
        );
        focusables.forEach((el) => {
          el.removeAttribute('tabindex');
          el.style.pointerEvents = '';
        });
      }
    });
  }

  resolveVariantInput(fromNode) {
    if (!fromNode) return null;

    if (fromNode.matches?.(this.selectors.variantInput)) return fromNode;

    const byPrev = fromNode.previousElementSibling;
    if (byPrev?.matches?.(this.selectors.variantInput)) return byPrev;

    const label = fromNode.closest('label');
    if (label) {
      const inputInLabel = label.querySelector(this.selectors.variantInput);
      if (inputInLabel) return inputInLabel;

      if (label.previousElementSibling?.matches?.(this.selectors.variantInput)) {
        return label.previousElementSibling;
      }
    }

    const group = fromNode.closest(this.selectors.variantGroup) || this;
    const checkedInGroup = group.querySelector(`${this.selectors.variantInput}[type="radio"]:checked`);
    if (checkedInGroup) return checkedInGroup;

    const anyInput = group.querySelector(this.selectors.variantInput);
    if (anyInput) return anyInput;

    return null;
  }

  onVariantChange(event) {
    const inputEl = this.resolveVariantInput(event.target);
    if (!inputEl) return;

    this.variantLayout = inputEl.getAttribute("data-variant-layout") || this.getAttribute("data-variant-layout") || '';

    switch (this.variantLayout) {
      case "chips": {
        const group = inputEl.closest(this.selectors.variantGroup);
        const current = group?.querySelector(".chip--selected");
        current?.classList.remove("chip--selected");

        const btn = inputEl.parentElement?.querySelector("[js-chip]");
        btn?.classList.add("chip--selected");
        break;
      }

      case "swatches": {
        const group = inputEl.closest(this.selectors.variantGroup);
        const current = group?.querySelector(".swatch--selected");
        current?.classList.remove("swatch--selected");

        const btn = inputEl.parentElement?.querySelector("[js-swatch]");
        btn?.classList.add("swatch--selected");
        break;
      }
    }

    const currentMainProductSection = inputEl.closest("[js-product-section]");
    const productWithinQuickView =
      currentMainProductSection?.dataset?.sectionType === "quick-view-product";

    const variantChangingEvent = new CustomEvent(EVENTS.PRODUCT_VARIANT_CHANGING, {
      bubbles: true,
      detail: {
        newOptionInput: inputEl,
        withinQuickView: productWithinQuickView,
      },
    });

    currentMainProductSection?.dispatchEvent(variantChangingEvent);
  }

  onVariantChangeA11y(inputHtml) {
    const inputEl = this.resolveVariantInput(inputHtml);
    if (!inputEl) return;

    const currentMainProductSection = this.closest("[js-product-section]");
    const productWithinQuickView =
      currentMainProductSection?.dataset?.sectionType === "quick-view-product";

    const variantChangingEvent = new CustomEvent(EVENTS.PRODUCT_VARIANT_CHANGING, {
      bubbles: true,
      detail: {
        newOptionInput: inputEl,
        withinQuickView: productWithinQuickView,
      },
    });

    currentMainProductSection.dispatchEvent(variantChangingEvent);
  }

  handleRepaint() {
    this.activeVariantsController.abort();
    this.initActiveVariants();
  }

  disconnectedCallback() {
    this.controller.abort();
  }
}

if (!customElements.get("product-block-variant-selector")) {
  customElements.define("product-block-variant-selector", ProductBlockVariantSelector);
}
